import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { AddIcon } from '@common/utils/icons/icons';
import OrganizationsForm from '../../organizations/OrganizationForm';
import { useAppDispatch, useAppSelector } from '../../../store';
import { selectIsUserAdminOrHigher } from '../../../store/general/general-slice-selectors';
import OrganizationsTable from '../../organizations/OrganizationsTable';
import { useSearchParams } from 'react-router-dom';
import { TAB_QUERY_STRING } from '../../../utils/url-tabs-helper';
import { a11yProps } from '../../misc/TabsPanel';
import OrganizationRole from '../../../models/enums/organization-roles';
import { selectOrganizationsData } from '../../../store/organization/organization-slice-selectors';
import { Theme, useMediaQuery } from '@mui/material';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { getAllOrganizationsById } from '../../../store/organization/organization-actions';

enum PageTabs {
  Importers = 'importers',
  Wholesalers = 'wholesalers',
  Distributors = 'dealers',
  Customers = 'organizations',
  Locations = 'locations',
}

const OrganizationsPage: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [filterParams, setFilterParams] = useSearchParams();
  const pageTab: PageTabs | null = filterParams.get(TAB_QUERY_STRING) as PageTabs;
  const matchesMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));
  const organization = useAppSelector((state) => selectOrganizationsData(state));

  const [isOrganizationFormOpen, setIsOrganizationFormOpen] = useState(false);

  const tabChangeHandler = useCallback(
    (tab: PageTabs) => {
      setFilterParams((prevState) => {
        prevState.set(TAB_QUERY_STRING, tab);
        return prevState;
      });
    },
    [setFilterParams],
  );

  const filteredTabs = useMemo(() => {
    const tabs: PageTabs[] = [];
    const currentOrgRole = organization.current?.role;
    if (currentOrgRole == null) return tabs;

    tabs.push(PageTabs.Locations);
    if (currentOrgRole === OrganizationRole.CUSTOMER) {
      return tabs;
    }
    tabs.push(PageTabs.Customers);
    if (currentOrgRole === OrganizationRole.DISTRIBUTOR) {
      return tabs.reverse();
    }
    tabs.push(PageTabs.Distributors);
    if (currentOrgRole === OrganizationRole.WHOLESALER) {
      return tabs.reverse();
    }
    tabs.push(PageTabs.Wholesalers);
    if (currentOrgRole === OrganizationRole.IMPORTER) {
      return tabs.reverse();
    }
    tabs.push(PageTabs.Importers);
    return tabs.reverse();
  }, [organization]);

  useEffect(() => {
    if (filteredTabs.length > 0 && !filteredTabs.includes(pageTab)) {
      tabChangeHandler(filteredTabs[0]);
    }
  }, [filteredTabs, pageTab, tabChangeHandler]);

  const openOrganizationFormClickHandler = () => {
    setIsOrganizationFormOpen(true);
  };

  useEffect(() => {
    if (
      organization.rest.getAllOrganizationsStatus === HttpFetchStatus.Idle &&
      organization.current?.id != null
    ) {
      dispatch(
        getAllOrganizationsById({
          org: organization.current.id,
        }),
      );
    }
  }, [dispatch, organization]);

  const closeOrganizationFormHandler = () => {
    setIsOrganizationFormOpen(false);
  };

  const tabChangeClickHandler = (_event: React.SyntheticEvent, newValue: number) => {
    tabChangeHandler(newValue > filteredTabs.length - 1 ? filteredTabs[0] : filteredTabs[newValue]);
  };

  let organizationRole = OrganizationRole.LOCATION;
  switch (pageTab) {
    case PageTabs.Customers:
      organizationRole = OrganizationRole.CUSTOMER;
      break;
    case PageTabs.Distributors:
      organizationRole = OrganizationRole.DISTRIBUTOR;
      break;
    case PageTabs.Wholesalers:
      organizationRole = OrganizationRole.WHOLESALER;
      break;
    case PageTabs.Importers:
      organizationRole = OrganizationRole.IMPORTER;
      break;
    default:
      break;
  }

  return (
    <React.Fragment>
      <Stack spacing={2} p={2}>
        <Typography variant="h2" fontSize={24} lineHeight={2}>
          {`${organization.current?.name ?? ''} - ${
            organization.current?.role === OrganizationRole.CUSTOMER
              ? t('title-locations')
              : t('button-organizations')
          }`}
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          {organization.current?.role === OrganizationRole.CUSTOMER ? (
            <Typography
              variant="h3"
              sx={{
                alignItems: 'center',
                display: 'flex',
              }}
            >
              {t('title-locations')}
            </Typography>
          ) : (
            <Tabs
              value={Math.max(
                filteredTabs.findIndex((ft) => ft === pageTab),
                0,
              )}
              onChange={tabChangeClickHandler}
              allowScrollButtonsMobile
              variant="scrollable"
              scrollButtons={false}
            >
              {filteredTabs.map((tabValue, index) => (
                <Tab key={tabValue} label={t('title-' + tabValue)} {...a11yProps(index)} />
              ))}
            </Tabs>
          )}

          {isAdmin &&
            (matchesMdDown ? (
              <IconButton onClick={openOrganizationFormClickHandler} color="primary">
                {<AddIcon />}
              </IconButton>
            ) : (
              <Button startIcon={<AddIcon />} onClick={openOrganizationFormClickHandler}>
                {t('button-add-' + organizationRole)}
              </Button>
            ))}
        </Stack>
        <Stack spacing={2} height="100%" width="100%">
          <OrganizationsTable role={organizationRole} />
        </Stack>
      </Stack>
      {organization.current != null && (
        <OrganizationsForm
          organization={null}
          parentId={organization.current.id}
          open={isOrganizationFormOpen}
          role={organizationRole}
          onClose={closeOrganizationFormHandler}
        />
      )}
    </React.Fragment>
  );
};

export default OrganizationsPage;
